// One tiny data hook instead of a query library. The app has ~8 read endpoints
// and no mutations yet; TanStack Query would be more machinery than it earns.
// Swap this out — not the call sites — if caching or invalidation becomes real.

import { useCallback, useEffect, useRef, useState } from 'react'

export interface AsyncState<T> {
  data: T | null
  error: unknown
  loading: boolean
  reload: () => void
}

export function useAsync<T>(fn: () => Promise<T>, deps: unknown[]): AsyncState<T> {
  const [data, setData] = useState<T | null>(null)
  const [error, setError] = useState<unknown>(null)
  const [loading, setLoading] = useState(true)
  const [nonce, setNonce] = useState(0)

  // Keeps a stale response from a superseded request from overwriting a newer one.
  const seq = useRef(0)
  const fnRef = useRef(fn)
  fnRef.current = fn

  useEffect(() => {
    const mine = ++seq.current
    setLoading(true)
    setError(null)
    fnRef
      .current()
      .then((d) => {
        if (seq.current === mine) {
          setData(d)
          setLoading(false)
        }
      })
      .catch((e) => {
        if (seq.current === mine) {
          setError(e)
          setLoading(false)
        }
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce])

  const reload = useCallback(() => setNonce((n) => n + 1), [])
  return { data, error, loading, reload }
}
